import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from "../components/layout/Layout";
import { useInfiniteClients } from '../hooks/useClients';
import { ClientFilters, ClientSpaceInfo } from '../types';
import { clientService } from '../services/clientService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../contexts/ToastContext';
import WarehouseUtilizationBar from '../components/common/WarehouseUtilizationBar';
import MobileCardList, { TableColumn } from '../components/common/MobileCardList';
import ResponsiveSelect from '../components/common/ResponsiveSelect';
import CreateClient from './client-create';

export default function ClientManagement() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Omit<ClientFilters, 'page'>>({
    limit: 25,
    sort: 'createdAt',
    order: 'DESC',
  });
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [spaceInfo, setSpaceInfo] = useState<ClientSpaceInfo | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpen, setSearchOpen] = useState(true);
  const [sortBy, setSortBy] = useState<'createdAt' | 'companyName' | 'email' | 'status'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [isExporting, setIsExporting] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClientId, setEditingClientId] = useState<number | null>(null);

  const { data: clientsData, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteClients(filters);

  // Fetch warehouse utilization
  const { data: warehouseUtilization, isLoading: isLoadingUtilization } = useQuery({
    queryKey: ['warehouse-utilization'],
    queryFn: () => clientService.getWarehouseUtilization(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Hide body scrollbar on mount, restore on unmount
  useEffect(() => {
    // Add scrollbar-hide class to body and html
    document.body.classList.add('scrollbar-hide');
    document.documentElement.classList.add('scrollbar-hide');

    return () => {
      // Restore scrollbar on unmount
      document.body.classList.remove('scrollbar-hide');
      document.documentElement.classList.remove('scrollbar-hide');
    };
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Export to CSV
  const exportToCSV = async () => {
    if (!hasPermission('client:export')) {
      return;
    }

    try {
      setIsExporting(true);
      await clientService.exportClientsCsv({
        search: searchTerm || undefined,
        sort: sortBy,
        order: sortOrder,
      });
    } catch (error) {
      console.error('CSV export failed:', error);
      toast.error('Failed to export clients. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Auto-apply filters when search/sort values change
  useEffect(() => {
    setFilters({
      limit: 25,
      search: searchTerm.trim() || undefined,
      sort: sortBy,
      order: sortOrder,
    });
  }, [searchTerm, sortBy, sortOrder]);

  // Clear all filters
  const clearFilters = () => {
    setSortBy('createdAt');
    setSortOrder('DESC');
  };

  // Handle view client details - fetch full details from API
  const handleViewClient = useCallback(async (client: any) => {
    setSpaceInfo(null);
    try {
      // Fetch full client details including relations using the service
      const fullClientData = await clientService.getClient(client.id);
      setSelectedClient(fullClientData);
      setShowViewModal(true);
    } catch (error) {
      console.error('Error fetching client details:', error);
      // Fallback to basic client data from table
      setSelectedClient(client);
      setShowViewModal(true);
    }

    // Load current space usage (pallets/sqft in use) independently of the detail fetch
    try {
      const info = await clientService.getClientSpaceInfo(client.id);
      setSpaceInfo(info);
    } catch (error) {
      console.error('Error fetching client space info:', error);
      setSpaceInfo(null);
    }
  }, []);

  // Handle add client - open the onboarding form overlay in create mode
  const handleAddClient = () => {
    setEditingClientId(null);
    setShowClientForm(true);
  };

  // Handle edit client - open the same form overlay in edit mode
  const handleEditClient = (client: any) => {
    setEditingClientId(client.id);
    setShowClientForm(true);
  };

  // Close the client form overlay
  const closeClientForm = () => {
    setShowClientForm(false);
    setEditingClientId(null);
  };

  // Close modal
  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedClient(null);
    setSpaceInfo(null);
  };

  // Debug logging
  console.log('ClientManagement - clientsData:', clientsData);
  console.log('ClientManagement - isLoading:', isLoading);
  console.log('ClientManagement - isError:', isError);
  console.log('ClientManagement - error:', error);

  // Memoize clients data to prevent unnecessary re-renders
  const clients = useMemo(() => {
    if (!clientsData) return [];

    // Flatten all pages of data
    return clientsData.pages.flatMap(page => page.data || []);
  }, [clientsData]);

  // Shared by the desktop grid rows and the mobile card list
  const clientColumns: TableColumn[] = [
    {
      key: 'companyName',
      label: 'Company Name',
      render: (value, client) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate" title={value}>{value}</p>
          <p className="text-xs text-gray-500 truncate">{client.registrationNumber || 'No reg.'}</p>
        </div>
      ),
    },
    {
      key: 'contactPersonName',
      label: 'Contact Person',
      render: (value, client) => (
        <div className="min-w-0">
          <p className="text-sm text-gray-900 truncate" title={value}>{value}</p>
          <p className="text-xs text-gray-500 truncate">{client.designation || 'No designation'}</p>
        </div>
      ),
    },
    { key: 'email', label: 'Email', render: (value) => <span className="break-all">{value}</span> },
    { key: 'phone', label: 'Phone' },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className={`px-3 py-1.5 inline-flex text-xs font-semibold rounded-full whitespace-nowrap shadow-sm
          ${value === 'active' ? 'bg-green-100 text-green-700 border border-green-200' :
            value === 'pending' ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' :
            value === 'inactive' ? 'bg-gray-100 text-gray-700 border border-gray-200' :
            'bg-red-100 text-red-700 border border-red-200'}`}
        >
          {value?.charAt(0).toUpperCase() + value?.slice(1)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, client) => (
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={() => handleViewClient(client)}
            className="min-h-[40px] px-3 py-2 rounded-lg bg-brand-50 text-brand-600 text-xs font-semibold flex items-center gap-1.5 active:bg-brand-600 active:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View
          </button>
          {hasPermission('client:update') && (
            <button
              onClick={() => handleEditClient(client)}
              className="min-h-[40px] px-3 py-2 rounded-lg bg-green-50 text-green-600 text-xs font-semibold flex items-center gap-1.5 active:bg-green-600 active:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
          )}
          {hasPermission('inventory:view') && (
            <button
              onClick={() => navigate(`/clients-inventory/${client.id}`)}
              className="min-h-[40px] px-3 py-2 rounded-lg bg-purple-50 text-purple-600 text-xs font-semibold flex items-center gap-1.5 active:bg-purple-600 active:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Stock
            </button>
          )}
        </div>
      ),
    },
  ];

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen p-6 bg-gradient-to-br from-gray-50 via-brand-50/30 to-gray-50 flex items-center justify-center">
          <div className="text-center bg-white p-10 rounded-2xl shadow-2xl border border-gray-200">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-200 border-t-blue-600 mx-auto mb-6"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-8 h-8 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
            <p className="text-gray-800 font-semibold text-lg">Loading client data...</p>
            <p className="text-gray-500 text-sm mt-2">Please wait while we fetch your clients</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (isError) {
    return (
      <Layout>
        <div className="min-h-screen p-6 bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <p className="text-red-600 font-medium">Failed to load client data</p>
            <p className="text-gray-600 text-sm mt-2">{(error as any)?.message}</p>
            <div className="flex gap-2 justify-center mt-4">
              <button 
                onClick={() => window.location.reload()} 
                className="btn-primary"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="min-h-screen px-3 py-4 lg:p-4 bg-gradient-to-br from-gray-50 to-gray-100 scrollbar-hide overflow-x-hidden" style={{ overflowY: 'auto' }}>
        {/* Header with enhanced shadow */}
        <div className="mb-4">
          <div className="flex items-center space-x-3 bg-gradient-to-r from-brand-100 to-brand-50 p-4 sm:p-5 rounded-xl shadow-lg border-2 border-brand-300">
            <div className="p-2.5 sm:p-3 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl shadow-md flex-shrink-0">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Client Management</h2>
              <p className="text-gray-600 mt-0.5 sm:mt-1 text-sm sm:text-base">Manage and monitor your warehouse clients</p>
            </div>
          </div>
        </div>

        {/* Warehouse Utilization Bar */}
        <div className="mb-4">
          <WarehouseUtilizationBar
            utilization={warehouseUtilization ?? null}
            isLoading={isLoadingUtilization}
            compact
          />
        </div>

        {/* Client Table Card with enhanced shadows and borders */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:border-primary-300">
          {/* Header with Search and Actions in One Row */}
          <div className="p-4 sm:p-5 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300">
            <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:gap-4">
              {/* Left: Title */}
              <div className="flex-shrink-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex flex-wrap items-center gap-2">
                  Client List
                  {searchTerm && (
                    <span className="px-3 py-1 text-xs font-semibold bg-green-100 text-green-700 rounded-full flex items-center shadow-sm">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                      </svg>
                      Filtered
                    </span>
                  )}
                </h3>
              </div>

              {/* Center: Search Bar */}
              <div className="w-full lg:flex-1 lg:min-w-[200px] lg:max-w-md min-w-0">
                {searchOpen ? (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search by company, email, contact, phone..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="block w-full pl-10 pr-16 py-2.5 border-2 border-gray-300 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 hover:border-gray-400"
                      autoComplete="off"
                      data-form-type="other"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-0.5">
                      {searchInput && (
                        <button
                          onClick={() => { setSearchInput(''); setSearchTerm(''); }}
                          title="Clear search"
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => { setSearchInput(''); setSearchTerm(''); setSearchOpen(false); }}
                        title="Close search bar"
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setSearchOpen(true)}
                    className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-all text-sm font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Search
                  </button>
                )}
              </div>

              {/* Right: Action Buttons */}
              <div className="flex flex-col w-full gap-2 lg:flex-row lg:items-center lg:gap-3 lg:w-auto lg:flex-shrink-0">
                {hasPermission('client:create') && (
                  <button
                    onClick={handleAddClient}
                    className="btn-primary order-1 w-full min-h-[44px] lg:order-3 lg:w-auto transform transition-all duration-300 lg:hover:scale-105 lg:hover:shadow-lg"
                  >
                    Add Client
                  </button>
                )}

                <div className="order-2 grid grid-cols-2 gap-2 lg:contents">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-2.5 min-h-[44px] lg:min-h-0 text-sm font-medium border rounded-xl flex items-center justify-center gap-2 transition-all duration-200 shadow-sm ${
                    showFilters
                      ? 'bg-gradient-to-r from-brand-500 to-brand-600 border-brand-600 text-white shadow-blue-200'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-md'
                  }`}
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                  </svg>
                  <span className="truncate">Sort</span>
                </button>

                <button
                  onClick={exportToCSV}
                  disabled={isExporting || !hasPermission('client:export')}
                  className="px-4 py-2.5 min-h-[44px] lg:min-h-0 text-sm font-medium border-2 border-gray-300 rounded-xl bg-white text-gray-700 hover:bg-gray-50 hover:border-primary-400 hover:shadow-md flex items-center justify-center gap-2 transition-all duration-300 lg:hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={
                    hasPermission('client:export')
                      ? 'Export client data to CSV'
                      : 'You need client:export permission to export'
                  }
                >
                  {isExporting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="truncate">Exporting...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                      </svg>
                      <span className="truncate">Export</span>
                    </>
                  )}
                </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sort Options Panel */}
          {showFilters && (
            <div className="bg-gradient-to-br from-brand-50 to-white p-5 mx-4 mb-4 rounded-xl border-2 border-brand-200 shadow-lg animate-in slide-in-from-top duration-300">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-primary-600 rounded-lg mr-3 shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                  </svg>
                </div>
                <h4 className="text-base font-semibold text-gray-900">Sort Options</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Sort By */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1.5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                    </svg>
                    Sort By
                  </label>
                  <ResponsiveSelect
                    options={[
                      { value: 'createdAt', label: 'Created Date' },
                      { value: 'companyName', label: 'Company Name' },
                      { value: 'email', label: 'Email' },
                      { value: 'status', label: 'Status' },
                    ]}
                    value={sortBy}
                    onChange={(val) => setSortBy(val as any)}
                    includeEmpty={false}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                  />
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1.5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                    Order
                  </label>
                  <ResponsiveSelect
                    options={[
                      { value: 'ASC', label: 'Ascending (A-Z, Old-New)' },
                      { value: 'DESC', label: 'Descending (Z-A, New-Old)' },
                    ]}
                    value={sortOrder}
                    onChange={(val) => setSortOrder(val as 'ASC' | 'DESC')}
                    includeEmpty={false}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                  />
                </div>
              </div>

              {/* Sort Actions */}
              <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={clearFilters}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Reset Sort</span>
                </button>
              </div>
            </div>
          )}

          {/* Modern Table Design */}
          <div className="overflow-hidden">
            {/* Mobile Card List */}
            <div className="p-3 lg:hidden">
              <MobileCardList
                columns={clientColumns}
                data={clients}
                isLoading={isFetchingNextPage}
                onLoadMore={() => fetchNextPage()}
                hasMore={!!hasNextPage}
                emptyMessage={searchTerm ? 'No clients found. Try adjusting your search terms.' : 'No clients found. Add your first client to get started.'}
              />
            </div>

            {/* Table Header */}
            <div className="hidden lg:block bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
              <div className="grid grid-cols-12 gap-8 px-8 py-4 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                <div className="col-span-3">Company Name</div>
                <div className="col-span-2">Contact Person</div>
                <div className="col-span-2">Email</div>
                <div className="col-span-2">Phone</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2 text-center">Actions</div>
              </div>
            </div>

            {/* Table Body with Infinite Scroll */}
            <div
              className="hidden lg:block divide-y divide-gray-100 scrollbar-hide"
              style={{ maxHeight: '600px', overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onScroll={(e) => {
                const target = e.currentTarget;
                const scrollPercentage = (target.scrollTop / (target.scrollHeight - target.clientHeight)) * 100;
                
                // Load more when user scrolls to 90% of the content
                if (scrollPercentage > 90 && hasNextPage && !isFetchingNextPage) {
                  fetchNextPage();
                }
              }}
            >
              {clients.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No clients found</h3>
                  <p className="text-gray-500 text-sm">
                    {searchTerm ? 'Try adjusting your search terms' : 'Add your first client to get started.'}
                  </p>
                </div>
              ) : (
                <>
                  {clients.map((client: any, index: number) => (
                  <div 
                    key={client.id} 
                    className="grid grid-cols-12 gap-8 px-8 py-5 hover:bg-gradient-to-r hover:from-brand-50 hover:to-transparent transition-all duration-300 border-b border-gray-200 last:border-b-0 group hover:shadow-md hover:scale-[1.01] transform"
                  >
                    {/* Company Name */}
                    <div className="col-span-3 flex items-center">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors duration-200" title={client.companyName}>
                          {client.companyName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {client.registrationNumber || 'No reg.'}
                        </p>
                      </div>
                    </div>

                    {/* Contact Person */}
                    <div className="col-span-2 flex items-center">
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 truncate" title={client.contactPersonName}>
                          {client.contactPersonName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {client.designation || 'No designation'}
                        </p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="col-span-2 flex items-center">
                      <p className="text-sm text-gray-700 truncate" title={client.email}>
                        {client.email}
                      </p>
                    </div>

                    {/* Phone */}
                    <div className="col-span-2 flex items-center">
                      <p className="text-sm text-gray-700 truncate" title={client.phone}>
                        {client.phone}
                      </p>
                    </div>

                    {/* Status */}
                    <div className="col-span-1 flex items-center">
                      <span className={`px-3 py-1.5 inline-flex text-xs font-semibold rounded-full whitespace-nowrap shadow-sm group-hover:shadow-md transition-shadow duration-200
                        ${client.status === 'active' ? 'bg-green-100 text-green-700 border border-green-200' : 
                          client.status === 'pending' ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' :
                          client.status === 'inactive' ? 'bg-gray-100 text-gray-700 border border-gray-200' : 
                          'bg-red-100 text-red-700 border border-red-200'}`}
                      >
                        {client.status?.charAt(0).toUpperCase() + client.status?.slice(1)}
                      </span>
                    </div>

                    {/* Actions - Modern Icon Buttons */}
                    <div className="col-span-2 flex items-center justify-center gap-2">
                      <button 
                        onClick={() => handleViewClient(client)}
                        className="group relative p-2 rounded-lg bg-brand-50 text-brand-600 hover:bg-brand-600 hover:text-white transition-all duration-200 hover:scale-110 hover:shadow-lg"
                        title="View Details"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                          View
                        </span>
                      </button>
                      
                      {hasPermission('client:update') && (
                        <button 
                          onClick={() => handleEditClient(client)}
                          className="group relative p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-600 hover:text-white transition-all duration-200 hover:scale-110 hover:shadow-lg"
                          title="Edit Client"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Edit
                          </span>
                        </button>
                      )}
                      
                      {hasPermission('inventory:view') && (
                        <button 
                          onClick={() => navigate(`/clients-inventory/${client.id}`)}
                          className="group relative p-2 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-600 hover:text-white transition-all duration-200 hover:scale-110 hover:shadow-lg"
                          title="View Inventory"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                          <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Stock
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                  ))}
                  
                  {/* Loading Indicator */}
                  {isFetchingNextPage && (
                    <div className="flex items-center justify-center py-8 bg-gray-50">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                        <span className="text-sm text-gray-600 font-medium">Loading more clients...</span>
                      </div>
                    </div>
                  )}
                  
                  {/* End of List Indicator */}
                  {!hasNextPage && clients.length > 0 && (
                    <div className="flex items-center justify-center py-6 bg-gray-50">
                      <div className="text-center">
                        <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-gray-600 font-medium">All clients loaded</p>
                        <p className="text-xs text-gray-500 mt-1">Showing {clients.length} {clients.length === 1 ? 'client' : 'clients'}</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Client View Modal */}
        {showViewModal && selectedClient && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end lg:items-center justify-center z-50 p-3 lg:p-4">
            <div className="bg-white rounded-2xl lg:rounded-lg shadow-2xl max-w-4xl w-full max-h-[85vh] lg:max-h-[95vh] overflow-hidden animate-slide-up lg:animate-none flex flex-col">
              {/* Professional Header */}
              <div className="bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-4 lg:px-8 lg:py-6 text-white">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center min-w-0 flex-1">
                    <svg className="w-8 h-8 text-white mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-2xl font-bold text-white">Client Details</h2>
                      <p className="text-brand-100 text-sm mt-1 break-words">{selectedClient.companyName}</p>
                    </div>
                  </div>
                  <button
                    onClick={closeViewModal}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto flex-1 min-h-0 lg:flex-none lg:max-h-[calc(95vh-180px)] bg-gray-50">
                <div className="p-4 lg:p-8 space-y-4 lg:space-y-8">
                  {/* Company Information */}
                  <div className="bg-white rounded-lg shadow-sm p-4 lg:p-8">
                    <div className="flex items-center mb-6">
                      <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <h2 className="text-xl font-semibold text-gray-900">Company Information</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                        <p className="text-lg font-semibold text-gray-900 break-words">{selectedClient.companyName}</p>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tenant ID</label>
                        <p className="text-gray-900 break-words">{selectedClient.tenantId || 'Not assigned'}</p>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Warehouse</label>
                        <p className="text-gray-900 break-words">{selectedClient.warehouseName || (selectedClient.warehouseId ? `Warehouse #${selectedClient.warehouseId}` : 'Legacy / not assigned')}</p>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                        <p className="text-gray-900 break-words">{selectedClient.registrationNumber || 'Not provided'}</p>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                        <p className="text-gray-900 break-words">{selectedClient.gstNumber || 'Not provided'}</p>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
                        <p className="text-gray-900 break-words">{selectedClient.panNumber || 'Not provided'}</p>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full
                          ${
                            selectedClient.status === 'active' ? 'bg-green-100 text-green-800' : 
                            selectedClient.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            selectedClient.status === 'inactive' ? 'bg-gray-100 text-gray-800' : 
                            'bg-red-100 text-red-800'
                          }`}
                        >
                          {selectedClient.status.charAt(0).toUpperCase() + selectedClient.status.slice(1)}
                        </span>
                      </div>
                    </div>

                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Complete Address</label>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-gray-900 mb-2 break-words">{selectedClient.address}</p>
                        <p className="text-gray-600 text-sm break-words">
                          {selectedClient.city}, {selectedClient.state} - {selectedClient.pinCode}
                        </p>
                        <p className="text-gray-600 text-sm break-words">{selectedClient.country}</p>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-white rounded-lg shadow-sm p-4 lg:p-8">
                    <div className="flex items-center mb-6">
                      <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <h2 className="text-xl font-semibold text-gray-900">Contact Person Details</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person Name</label>
                        <p className="text-lg font-semibold text-gray-900 break-words">{selectedClient.contactPersonName}</p>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                        <p className="text-gray-900 break-words">{selectedClient.designation || 'Not specified'}</p>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                        <div className="flex items-center min-w-0">
                          <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <a href={`mailto:${selectedClient.email}`} className="text-brand-600 hover:text-brand-800 transition-colors break-all">
                            {selectedClient.email}
                          </a>
                        </div>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                        <div className="flex items-center min-w-0">
                          <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <a href={`tel:${selectedClient.phone}`} className="text-brand-600 hover:text-brand-800 transition-colors break-all">
                            {selectedClient.phone}
                          </a>
                        </div>
                      </div>
                      {selectedClient.alternatePhone && (
                        <div className="md:col-span-2 min-w-0">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Phone</label>
                          <div className="flex items-center min-w-0">
                            <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <a href={`tel:${selectedClient.alternatePhone}`} className="text-brand-600 hover:text-brand-800 transition-colors break-all">
                              {selectedClient.alternatePhone}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>


                  {/* Warehouse Space Allocation */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-brand-600 to-brand-700 px-6 py-4">
                      <div className="flex items-center">
                        <div className="p-2 bg-white/20 rounded-lg mr-3">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-white">Warehouse Space Allocation</h2>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      {(selectedClient.clientType && (selectedClient.palletCapacity || selectedClient.sqftAssigned)) ? (
                        <div className="space-y-6">
                          {/* Allocation Summary Cards */}
                          {selectedClient.clientType === 'both' ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Zone Storage (Sq Ft) */}
                            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-5">
                              <div className="flex items-center justify-between mb-3">
                                <label className="text-xs font-semibold text-green-700 uppercase tracking-wider">Zone Storage (Sq Ft)</label>
                                <span className="text-sm font-bold text-green-900">{spaceInfo ? `${spaceInfo.zoneUtilizationPercentage}%` : ''}</span>
                              </div>
                              <p className="text-3xl font-bold text-green-900">
                                {spaceInfo ? spaceInfo.zoneSqftUsed.toLocaleString() : '—'}
                                <span className="text-base font-medium text-green-600"> / {(selectedClient.sqftAssigned ?? 0).toLocaleString()}</span>
                              </p>
                              <p className="text-xs text-green-600 mt-1">Square feet used / allocated (zones)</p>
                            </div>

                            {/* Rack Storage (Pallets) */}
                            <div className="bg-gradient-to-br from-brand-50 to-brand-100 border border-brand-200 rounded-lg p-5">
                              <div className="flex items-center justify-between mb-3">
                                <label className="text-xs font-semibold text-brand-700 uppercase tracking-wider">Rack Storage (Pallets)</label>
                                <span className="text-sm font-bold text-brand-900">{spaceInfo ? `${spaceInfo.rackUtilizationPercentage}%` : ''}</span>
                              </div>
                              <p className="text-3xl font-bold text-brand-900">
                                {spaceInfo ? spaceInfo.rackPalletsUsed.toLocaleString() : '—'}
                                <span className="text-base font-medium text-brand-600"> / {(selectedClient.palletCapacity ?? 0).toLocaleString()}</span>
                              </p>
                              <p className="text-xs text-brand-600 mt-1">Pallet positions used / allocated (racks)</p>
                            </div>
                          </div>
                          ) : (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Allocation Type Card */}
                            <div className="bg-gradient-to-br from-brand-50 to-brand-100 border border-brand-200 rounded-lg p-5">
                              <div className="flex items-center justify-between mb-3">
                                <label className="text-xs font-semibold text-brand-700 uppercase tracking-wider">Allocation Type</label>
                                <div className="p-1.5 bg-brand-600 rounded-md">
                                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                  </svg>
                                </div>
                              </div>
                              <p className="text-xl font-bold text-brand-900">
                                {(selectedClient.clientType === 'per_sqft' || selectedClient.clientType === 'sqft') ? 'Per Square Feet' : 'Per Pallet'}
                              </p>
                              <p className="text-xs text-brand-600 mt-1">
                                {(selectedClient.clientType === 'per_sqft' || selectedClient.clientType === 'sqft') 
                                  ? 'Floor space based allocation' 
                                  : 'Pallet position based allocation'}
                              </p>
                            </div>

                            {/* Base Allocation Card */}
                            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-5">
                              <div className="flex items-center justify-between mb-3">
                                <label className="text-xs font-semibold text-green-700 uppercase tracking-wider">Base Allocation</label>
                                <div className="p-1.5 bg-green-600 rounded-md">
                                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                              </div>
                              <p className="text-3xl font-bold text-green-900">
                                {(selectedClient.clientType === 'per_sqft' || selectedClient.clientType === 'sqft')
                                  ? selectedClient.sqftAssigned?.toLocaleString() 
                                  : selectedClient.palletCapacity?.toLocaleString()}
                              </p>
                              <p className="text-xs text-green-600 mt-1">
                                {(selectedClient.clientType === 'per_sqft' || selectedClient.clientType === 'sqft') ? 'Square Feet' : 'Pallet Positions'}
                              </p>
                            </div>

                            {/* In Use Card - current usage from inventory */}
                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-5">
                              <div className="flex items-center justify-between mb-3">
                                <label className="text-xs font-semibold text-purple-700 uppercase tracking-wider">In Use</label>
                                <div className="p-1.5 bg-purple-600 rounded-md">
                                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                  </svg>
                                </div>
                              </div>
                              <p className="text-3xl font-bold text-purple-900">
                                {spaceInfo
                                  ? ((selectedClient.clientType === 'per_sqft' || selectedClient.clientType === 'sqft')
                                      ? spaceInfo.spaceUsed.toLocaleString()
                                      : spaceInfo.palletsUsed.toLocaleString())
                                  : '—'}
                              </p>
                              <p className="text-xs text-purple-600 mt-1">
                                {(selectedClient.clientType === 'per_sqft' || selectedClient.clientType === 'sqft') ? 'Square Feet' : 'Pallet Positions'}
                                {spaceInfo ? ` · ${spaceInfo.utilizationPercentage}% utilized` : ''}
                              </p>
                            </div>
                          </div>
                          )}

                          {/* Additional Space Section */}
                          {(selectedClient.clientType === 'both'
                            ? !!spaceInfo && (spaceInfo.rackPalletsUsed > spaceInfo.rackPalletLimit || spaceInfo.zoneSqftUsed > spaceInfo.zoneSqftLimit)
                            : selectedClient.additionalSpaceSqft > 0) && (
                            <div className="border-t border-gray-200 pt-6">
                              <div className="bg-gradient-to-br from-orange-50 to-orange-100 border-l-4 border-orange-500 rounded-lg p-5">
                                <div className="flex items-start">
                                  <div className="flex-shrink-0">
                                    <div className="p-2 bg-orange-500 rounded-lg">
                                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="ml-4 flex-1">
                                    <h4 className="text-base font-bold text-orange-900 mb-3">Additional Space Usage</h4>
                                    {selectedClient.clientType === 'both' ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                      <div className="bg-white/60 rounded-lg p-4 border border-orange-200">
                                        <label className="block text-xs font-semibold text-orange-700 uppercase tracking-wider mb-2">Additional Zone Space</label>
                                        <p className="text-2xl font-bold text-orange-900">
                                          {(spaceInfo ? Math.max(0, spaceInfo.zoneSqftUsed - spaceInfo.zoneSqftLimit) : 0).toLocaleString()}
                                        </p>
                                        <p className="text-xs text-orange-600 mt-1">Square Feet (zones)</p>
                                      </div>
                                      <div className="bg-white/60 rounded-lg p-4 border border-orange-200">
                                        <label className="block text-xs font-semibold text-orange-700 uppercase tracking-wider mb-2">Additional Rack Pallets</label>
                                        <p className="text-2xl font-bold text-orange-900">
                                          {(spaceInfo ? Math.max(0, spaceInfo.rackPalletsUsed - spaceInfo.rackPalletLimit) : 0).toLocaleString()}
                                        </p>
                                        <p className="text-xs text-orange-600 mt-1">Pallet Positions (racks)</p>
                                      </div>
                                    </div>
                                    ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                      <div className="bg-white/60 rounded-lg p-4 border border-orange-200">
                                        <label className="block text-xs font-semibold text-orange-700 uppercase tracking-wider mb-2">Additional Sqft</label>
                                        <p className="text-2xl font-bold text-orange-900">
                                          {Number(selectedClient.additionalSpaceSqft || 0).toLocaleString()}
                                        </p>
                                        <p className="text-xs text-orange-600 mt-1">Square Feet</p>
                                      </div>
                                      <div className="bg-white/60 rounded-lg p-4 border border-orange-200">
                                        <label className="block text-xs font-semibold text-orange-700 uppercase tracking-wider mb-2">Equivalent Pallets</label>
                                        <p className="text-2xl font-bold text-orange-900">
                                          {Math.floor(Number(selectedClient.additionalSpaceSqft || 0) / 25).toLocaleString()}
                                        </p>
                                        <p className="text-xs text-orange-600 mt-1">Pallet Positions</p>
                                      </div>
                                    </div>
                                    )}
                                    <div className="bg-orange-100/80 rounded-lg p-3 border border-orange-300">
                                      <p className="text-xs text-orange-800 leading-relaxed">
                                        This client is currently using space beyond their base allocation. The system automatically tracks overflow usage for billing and capacity planning purposes.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Total Space Summary — hidden for 'both' (per-dimension meters above already show totals) */}
                              {selectedClient.clientType !== 'both' && (
                              <div className="mt-4 bg-gradient-to-br from-brand-50 to-brand-100 border border-brand-200 rounded-lg p-5">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <label className="block text-xs font-semibold text-brand-700 uppercase tracking-wider mb-2">Total Space in Use</label>
                                    <p className="text-3xl font-bold text-brand-900">
                                      {(selectedClient.clientType === 'per_sqft' || selectedClient.clientType === 'sqft')
                                        ? ((Number(selectedClient.sqftAssigned) || 0) + (Number(selectedClient.additionalSpaceSqft) || 0)).toLocaleString()
                                        : ((Number(selectedClient.palletCapacity) || 0) + Math.floor((Number(selectedClient.additionalSpaceSqft) || 0) / 25)).toLocaleString()
                                      }
                                    </p>
                                    <p className="text-xs text-brand-600 mt-1">
                                      {(selectedClient.clientType === 'per_sqft' || selectedClient.clientType === 'sqft') ? 'Square Feet' : 'Pallet Positions'} (Base + Additional)
                                    </p>
                                  </div>
                                  <div className="p-3 bg-brand-600 rounded-lg">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                              )}
                            </div>
                          )}

                          {/* Status Note - Only when no additional space */}
                          {(selectedClient.clientType === 'both'
                            ? !spaceInfo || (spaceInfo.rackPalletsUsed <= spaceInfo.rackPalletLimit && spaceInfo.zoneSqftUsed <= spaceInfo.zoneSqftLimit)
                            : (!selectedClient.additionalSpaceSqft || selectedClient.additionalSpaceSqft === 0)) && (
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                              <div className="flex items-start">
                                <div className="flex-shrink-0">
                                  <div className="p-1.5 bg-green-600 rounded-lg">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm text-green-800 leading-relaxed">
                                    This client has been allocated dedicated warehouse space for their operations.
                                    {(selectedClient.clientType === 'per_sqft' || selectedClient.clientType === 'sqft')
                                      ? ' The allocated floor space can be used for inventory storage and operations.'
                                      : ' The allocated pallet positions provide structured storage capacity for goods.'
                                    }
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-l-4 border-gray-400 rounded-lg p-6">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <div className="p-2 bg-gray-400 rounded-lg">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                </svg>
                              </div>
                            </div>
                            <div className="ml-4 flex-1">
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Space Allocation</h3>
                              <p className="text-gray-700 mb-4 leading-relaxed">
                                This client does not have any warehouse space allocated yet. Space allocation can be configured during client onboarding or updated later.
                              </p>
                              <div className="bg-white border border-gray-300 rounded-lg p-4">
                                <p className="text-sm font-semibold text-gray-900 mb-3">Available Allocation Options:</p>
                                <div className="space-y-2">
                                  <div className="flex items-start">
                                    <div className="flex-shrink-0 mt-0.5">
                                      <div className="w-1.5 h-1.5 bg-gray-600 rounded-full"></div>
                                    </div>
                                    <p className="ml-3 text-sm text-gray-700">
                                      <span className="font-semibold">Per Square Feet</span> - For floor space allocation
                                    </p>
                                  </div>
                                  <div className="flex items-start">
                                    <div className="flex-shrink-0 mt-0.5">
                                      <div className="w-1.5 h-1.5 bg-gray-600 rounded-full"></div>
                                    </div>
                                    <p className="ml-3 text-sm text-gray-700">
                                      <span className="font-semibold">Per Pallet</span> - For structured storage capacity
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* System Information */}
                  {selectedClient.createdAt && (
                    <div className="bg-white rounded-lg shadow-sm p-4 lg:p-8">
                      <div className="flex items-center mb-6">
                        <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h2 className="text-xl font-semibold text-gray-900">System Information</h2>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                          <div className="flex items-center text-gray-600">
                            <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {new Date(selectedClient.createdAt).toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                          <div className="flex items-center text-gray-600">
                            <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {new Date(selectedClient.updatedAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex justify-end space-x-4 px-4 py-4 lg:px-8 lg:py-6 bg-white border-t border-gray-200">
                <button
                  onClick={closeViewModal}
                  className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    closeViewModal();
                    handleEditClient(selectedClient);
                  }}
                  className="px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Client
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add / Edit Client Form Overlay */}
        {showClientForm && (
          <CreateClient
            clientId={editingClientId ?? undefined}
            onClose={closeClientForm}
            onSuccess={closeClientForm}
          />
        )}

      </div>

    </Layout>
  );
}
